import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApis } from '../../api/useApiClient';
import { useNotification } from '../../context/NotificationContext';

import {
    Box,
    Paper,
    Typography,
    Grid,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Stack,
    Skeleton,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import JobCardIcon from '@mui/icons-material/FactCheck';

import { formatPriceWithCommas } from '../utils/stringUtils';
import stringUtils from '../utils/stringUtils';

const STATUS_CONFIG = {
    DRAFT: { label: 'Draft', color: 'default' },
    IN_PROGRESS: { label: 'In Progress', color: 'warning' },
    CLOSED: { label: 'Closed', color: 'info' },
    INVOICED: { label: 'Invoiced', color: 'success' },
    COMPLETED: { label: 'Completed', color: 'success' }, // Legacy - kept for backward compatibility
    CANCELED: { label: 'Canceled', color: 'error' },
};

const ORIGIN_CONFIG = {
    POS: { label: 'Point of Sale', color: 'info' },
    JOB: { label: 'Job Card', color: 'secondary' },
};

const WorkOrderDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { tenantApi } = useApis();
    const { showNotification } = useNotification();

    const [loading, setLoading] = useState(true);
    const [workOrder, setWorkOrder] = useState(null);
    const [workOrderItems, setWorkOrderItems] = useState([]);

    useEffect(() => {
        loadWorkOrder();
    }, [id]);

    const loadWorkOrder = async () => {
        try {
            setLoading(true);
            const [workOrderRes, itemsRes] = await Promise.all([
                tenantApi(`/tenants/work-orders/${id}`),
                tenantApi(`/tenants/work-orders/${id}/items`)
            ]);
            setWorkOrder(workOrderRes.data);
            setWorkOrderItems(itemsRes.data || []);
        } catch (error) {
            console.error('Error loading work order:', error);
            showNotification(error.message || 'Failed to load work order', 'error');
        } finally {
            setLoading(false);
        }
    };

    const calculateSubTotal = () => {
        return workOrderItems.reduce((sum, item) => {
            const unitPrice = parseFloat(item.unitPrice || item.itemData?.unitPrice || 0);
            const quantity = parseFloat(item.quantity || 0);
            return sum + (unitPrice * quantity);
        }, 0);
    };

    const calculateTotalDiscount = () => {
        return workOrderItems.reduce((sum, item) => {
            const unitDiscount = parseFloat(item.unitDiscountAmount || 0);
            const quantity = parseFloat(item.quantity || 0);
            return sum + (unitDiscount * quantity);
        }, 0);
    };

    const calculateNetTotal = () => {
        return calculateSubTotal() - calculateTotalDiscount();
    };

    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1000px', mx: 'auto' }}>
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                    <Skeleton variant="text" width="40%" height={40} />
                    <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
                </Paper>
            </Box>
        );
    }

    if (!workOrder) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1000px', mx: 'auto' }}>
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography>Work order not found</Typography>
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate(-1)}
                        sx={{ mt: 2 }}
                    >
                        Go Back
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1000px', mx: 'auto' }}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                            <AssignmentIcon color="primary" />
                            <Typography variant="h5" fontWeight={600}>
                                ORDER - {workOrder.workOrderNumber}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Chip
                                label={STATUS_CONFIG[workOrder.status]?.label || workOrder.status}
                                color={STATUS_CONFIG[workOrder.status]?.color || 'default'}
                                size="small"
                            />
                            <Chip
                                label={ORIGIN_CONFIG[workOrder.originType]?.label || workOrder.originType}
                                color={ORIGIN_CONFIG[workOrder.originType]?.color || 'default'}
                                size="small"
                                variant="outlined"
                            />
                        </Stack>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {/* Continue in POS button for editable work orders originated from POS */}
                        {((workOrder.originType === 'POS' && ['DRAFT', 'IN_PROGRESS'].includes(workOrder.status)) ||
                          (workOrder.originType === 'JOB' && workOrder.status === 'CLOSED')) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PointOfSaleIcon />}
                                onClick={() => navigate(`/sales/pos/${workOrder.id}`)}
                            >
                                {workOrder.originType === 'JOB' ? 'Edit Pricing & Checkout' : 'Continue in POS'}
                            </Button>
                        )}

                        {/* Continue in Job Card button for editable work orders originated from a job card */}
                        {workOrder.originType === 'JOB' && ['DRAFT', 'IN_PROGRESS'].includes(workOrder.status) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<JobCardIcon />}
                                onClick={() => navigate(`/sales/job-cards/${workOrder.originId}`)}
                            >
                                View in Job Card
                            </Button>
                        )}

                        {/* Back button */}
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                    </Stack>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Work Order Info */}
                <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Location</Typography>
                        <Typography variant="body1">{workOrder.location?.name || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Created</Typography>
                        <Typography variant="body1">
                            {stringUtils.isoToLocalDateTime(workOrder.createdAt, { date: true, time: true })}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Customer</Typography>
                        <Typography variant="body1">{workOrder.customer?.name || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Vehicle</Typography>
                        <Typography variant="body1">
                            {workOrder.vehicle ? `${workOrder.vehicle.registrationNumber} - ${workOrder.vehicle.make || ''} ${workOrder.vehicle.model || ''}`.trim() : '-'}
                        </Typography>
                    </Grid>
                    {workOrder.invoiceId && (
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Invoice</Typography>
                            <Button
                                component={Link}
                                to={`/sales/invoices/${workOrder.invoiceId}`}
                                startIcon={<ReceiptIcon />}
                                size="small"
                            >
                                View Invoice
                            </Button>
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ mb: 3 }} />

                {/* Direct Items Table */}
                <Typography variant="h6" gutterBottom>
                    Items
                </Typography>
                <TableContainer sx={{ mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Discount</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {workOrderItems.length > 0 ? (
                                <>
                                    {workOrderItems.map((item, index) => {
                                        const unitPrice = parseFloat(item.unitPrice || item.itemData?.unitPrice || 0);
                                        const unitDiscount = parseFloat(item.unitDiscountAmount || 0);
                                        const quantity = parseFloat(item.quantity || 0);
                                        const lineTotal = (unitPrice - unitDiscount) * quantity;
                                        
                                        return (
                                            <TableRow key={item.id}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{item.itemData?.name || 'N/A'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={item.itemData?.type === 'SERVICE' ? 'Service' : 'Product'}
                                                        size="small"
                                                        variant="outlined"
                                                        color={item.itemData?.type === 'SERVICE' ? 'info' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatPriceWithCommas(unitPrice)}/{item.itemData?.uom}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {unitDiscount > 0 ? (
                                                        <Typography color="error">-{formatPriceWithCommas(unitDiscount)}</Typography>
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {item.quantity} {item.itemData?.uom}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography fontWeight="bold">
                                                        {formatPriceWithCommas(lineTotal.toFixed(2))}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {/* Subtotal row */}
                                    <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                        <TableCell colSpan={6} align="right">
                                            <Typography variant="body2">Subtotal</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {formatPriceWithCommas(calculateSubTotal().toFixed(2))}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    {/* Discount row (only if discount exists) */}
                                    {calculateTotalDiscount() > 0 && (
                                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                            <TableCell colSpan={6} align="right">
                                                <Typography variant="body2" color="error">Total Discount</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" color="error">
                                                    -{formatPriceWithCommas(calculateTotalDiscount().toFixed(2))}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {/* Net Total row */}
                                    <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                                        <TableCell colSpan={6} align="right">
                                            <Typography variant="subtitle1" fontWeight="bold">Net Total</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {formatPriceWithCommas(calculateNetTotal().toFixed(2))}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </>
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        No items in this work order
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Notes */}
                {workOrder.notes && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                        <Typography variant="body1">{workOrder.notes}</Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default WorkOrderDetails;
